import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Megaphone, Trash, PencilSimple, Plus, X, Image as ImageIcon, Users, UploadSimple } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EP = `${API_URL}/api/config/app-promos`;

const EMPTY = { title: '', audience_type: 'all', audience_segment_id: '', active: true, order: 0 };

export const AppPromosPanel = ({ lang = 'es' }) => {
  const es = lang === 'es';
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const L = {
    title: es ? 'Publicidad de la App' : 'App ads',
    intro: es ? 'Sube banners (imagen) que se mostrarán en el inicio de la App de voluntarios. Si hay varios activos se muestran en carrusel; si hay uno solo, como banner único.' : 'Upload image banners shown on the volunteer App home. Multiple active = carousel; one = single banner.',
    newPromo: es ? 'Nuevo anuncio' : 'New ad',
    editPromo: es ? 'Editar anuncio' : 'Edit ad',
    name: es ? 'Título (opcional)' : 'Title (optional)',
    image: es ? 'Imagen del banner' : 'Banner image',
    imageHint: es ? 'JPG / PNG / WEBP · máx 8 MB. Recomendado horizontal (p.ej. 1200×600).' : 'JPG / PNG / WEBP · max 8 MB. Landscape recommended (e.g. 1200×600).',
    pickImage: es ? 'Elegir imagen' : 'Choose image',
    replaceImage: es ? 'Cambiar imagen' : 'Replace image',
    active: es ? 'Activo' : 'Active',
    order: es ? 'Orden' : 'Order',
    audience: es ? 'Quién lo ve en la App' : 'Who sees it in the App',
    audienceAll: es ? 'Todos los voluntarios' : 'All volunteers',
    audienceSegment: es ? 'Solo un segmento' : 'Only a segment',
    pickSegment: es ? 'Elige un segmento…' : 'Pick a segment…',
    noSegments: es ? 'No hay segmentos. Creálos en Reclutamiento.' : 'No segments. Create them in Recruitment.',
    create: es ? 'Crear anuncio' : 'Create ad',
    save: es ? 'Guardar cambios' : 'Save changes',
    cancel: es ? 'Cancelar' : 'Cancel',
    empty: es ? 'Aún no hay anuncios.' : 'No ads yet.',
    needImage: es ? 'Elige una imagen' : 'Choose an image',
    created: es ? 'Anuncio creado' : 'Ad created',
    updated: es ? 'Anuncio actualizado' : 'Ad updated',
    deleted: es ? 'Anuncio eliminado' : 'Ad deleted',
    deleteConfirm: es ? '¿Eliminar este anuncio?' : 'Delete this ad?',
    all: es ? 'Todos' : 'All',
  };

  const load = async () => {
    try {
      const r = await axios.get(EP, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) { toast.error(errText(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/recruitment/segments`, { withCredentials: true })
      .then((r) => {
        const segs = r.data || [];
        setSegments(segs);
        segs.forEach((s) => {
          axios.get(`${API_URL}/api/recruitment/segments/${s.id}/count`, { withCredentials: true })
            .then((cr) => setSegmentCounts((p) => ({ ...p, [s.id]: cr.data?.total ?? null })))
            .catch(() => {});
        });
      })
      .catch(() => {});
  }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error(es ? 'Debe ser una imagen' : 'Must be an image'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error(es ? 'Máx 8 MB' : 'Max 8 MB'); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const resetForm = () => {
    setForm(EMPTY); setEditingId(null); setFile(null); setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      title: it.title || '', audience_type: it.audience_type === 'segment' ? 'segment' : 'all',
      audience_segment_id: it.audience_segment_id || '', active: !!it.active, order: it.order || 0,
    });
    setFile(null);
    setPreview(`${API_URL}${it.image_url}`);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
  };

  const submit = async () => {
    if (!editingId && !file) { toast.error(L.needImage); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${EP}/${editingId}`, {
          title: form.title, active: form.active, order: Number(form.order) || 0,
          audience_type: form.audience_type, audience_segment_id: form.audience_segment_id,
        }, { withCredentials: true });
        if (file) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${EP}/${editingId}/image`, fd, { withCredentials: true });
        }
        toast.success(L.updated);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', form.title);
        fd.append('audience_type', form.audience_type);
        fd.append('audience_segment_id', form.audience_segment_id);
        fd.append('active', form.active ? 'true' : 'false');
        fd.append('order', String(Number(form.order) || 0));
        await axios.post(EP, fd, { withCredentials: true });
        toast.success(L.created);
      }
      resetForm();
      load();
    } catch (e) { toast.error(errText(e)); }
    setSaving(false);
  };

  const toggleActive = async (it) => {
    try {
      await axios.patch(`${EP}/${it.id}/active`, { active: !it.active }, { withCredentials: true });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, active: !it.active } : x)));
    } catch (e) { toast.error(errText(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm(L.deleteConfirm)) return;
    try {
      await axios.delete(`${EP}/${it.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success(L.deleted);
    } catch (e) { toast.error(errText(e)); }
  };

  const segName = (id) => (segments.find((s) => s.id === id) || {}).name;

  return (
    <div className="space-y-6" data-testid="app-promos-panel">
      {/* Form */}
      <div className="rounded-sm border border-stone-200 p-4 space-y-4 bg-white">
        <div className="flex items-center gap-2">
          <Megaphone size={18} className="text-[#1f3e80]" />
          <h3 className="text-sm font-semibold text-stone-800">{editingId ? L.editPromo : L.newPromo}</h3>
        </div>
        <p className="text-xs text-stone-500">{L.intro}</p>

        <div>
          <Label className="text-sm">{L.image}</Label>
          <p className="text-xs text-stone-400 mb-1.5">{L.imageHint}</p>
          <div className="flex items-start gap-3">
            {preview ? (
              <img src={preview} alt="preview" className="h-24 w-40 object-cover rounded-sm border border-stone-200" data-testid="app-promo-preview" />
            ) : (
              <div className="h-24 w-40 rounded-sm border border-dashed border-stone-300 flex items-center justify-center text-stone-300">
                <ImageIcon size={28} />
              </div>
            )}
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="app-promo-file-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="app-promo-pick-image">
                <UploadSimple size={14} /> {editingId ? L.replaceImage : L.pickImage}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-sm">{L.name}</Label>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1" data-testid="app-promo-title-input" />
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="app-promo-active-switch" />
            <Label className="text-sm font-normal">{L.active}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-normal">{L.order}</Label>
            <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} className="w-20 h-8" data-testid="app-promo-order-input" />
          </div>
        </div>

        {/* Audiencia */}
        <div className="rounded-sm border border-stone-200 bg-stone-50/60 p-3 space-y-2">
          <Label className="text-sm font-medium text-stone-700 flex items-center gap-1.5"><Users size={15} /> {L.audience}</Label>
          <RadioGroup value={form.audience_type}
            onValueChange={(v) => setForm((f) => ({ ...f, audience_type: v, audience_segment_id: v === 'all' ? '' : f.audience_segment_id }))}
            className="gap-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="all" id="promo-aud-all" data-testid="app-promo-audience-all" />
              <Label htmlFor="promo-aud-all" className="text-sm font-normal cursor-pointer">{L.audienceAll}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="segment" id="promo-aud-seg" data-testid="app-promo-audience-segment" />
              <Label htmlFor="promo-aud-seg" className="text-sm font-normal cursor-pointer">{L.audienceSegment}</Label>
            </div>
          </RadioGroup>
          {form.audience_type === 'segment' && (
            segments.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">{L.noSegments}</p>
            ) : (
              <Select value={form.audience_segment_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, audience_segment_id: v }))}>
                <SelectTrigger className="bg-white" data-testid="app-promo-audience-select"><SelectValue placeholder={L.pickSegment} /></SelectTrigger>
                <SelectContent>
                  {segments.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`app-promo-segment-option-${s.id}`}>
                      {s.name}{segmentCounts[s.id] != null ? ` · ${segmentCounts[s.id]} ${es ? 'voluntarios' : 'volunteers'}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={submit} disabled={saving} className="gap-1.5 bg-[#1f3e80] hover:bg-[#1f3e80]/90 text-white" data-testid="app-promo-submit">
            <Plus size={16} /> {editingId ? L.save : L.create}
          </Button>
          {editingId && (
            <Button variant="ghost" onClick={resetForm} className="gap-1.5" data-testid="app-promo-cancel"><X size={14} /> {L.cancel}</Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-6">{L.empty}</p>
        ) : items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 border border-stone-200 rounded-sm p-2.5 bg-white" data-testid={`app-promo-row-${it.id}`}>
            <img src={`${API_URL}${it.image_url}`} alt={it.title} className="h-14 w-24 object-cover rounded-sm border border-stone-200 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-stone-800 truncate">{it.title || (es ? '(sin título)' : '(untitled)')}</span>
                <Badge variant="outline" className={it.active ? 'border-emerald-300 text-emerald-700 text-[11px]' : 'border-stone-300 text-stone-500 text-[11px]'}>
                  {it.active ? L.active : (es ? 'Inactivo' : 'Inactive')}
                </Badge>
                <Badge variant="outline" className="text-[11px] border-stone-300 text-stone-500">#{it.order}</Badge>
                {it.audience_type === 'segment' ? (
                  <Badge variant="outline" className="border-violet-300 text-violet-700 bg-white gap-1 text-[11px]" data-testid={`app-promo-audience-badge-${it.id}`}>
                    <Users size={12} /> {segName(it.audience_segment_id) || L.audienceSegment}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-sky-300 text-sky-700 bg-white text-[11px]">{L.all}</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Switch checked={it.active} onCheckedChange={() => toggleActive(it)} data-testid={`app-promo-active-${it.id}`} />
              <Button variant="ghost" size="sm" onClick={() => startEdit(it)} className="h-8 px-2 text-[#1f3e80]" data-testid={`app-promo-edit-${it.id}`}><PencilSimple size={15} /></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(it)} className="h-8 px-2 text-red-600 hover:bg-red-50" data-testid={`app-promo-delete-${it.id}`}><Trash size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppPromosPanel;
